<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from "vue";
import { mapAuthActions } from "@frontegg/vue";
import { Check } from "@lucide/vue";

import router from "../../router/router";
import { organizationsStore } from "../../stores/Utils/auth/Organizations";
import { useAuthStateStore } from "../../stores/Utils/auth/AuthState";
import useHasuraStore from "../../stores/hasura/hasura.store";
import gaiupApolloClient from "../../graphql/services/ApolloClient";
import { isSwitchingTenant } from "../../state/tenantSwitch";
import type Tenant from "../../types/auth/Tenant";

const emit = defineEmits<{ close: [] }>();

const orgStore = organizationsStore();
const authStore = useAuthStateStore();
const hasuraStore = useHasuraStore();
const tenants = computed<Tenant[]>(() =>
  [...(orgStore.getTenants() ?? [])].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }))
);
const currentTenantId = computed(() => orgStore.getCurrentTenant()?.tenantId);

const hoveredTenantId = ref<string | null>(null);
const activeTenantId = computed(() => hoveredTenantId.value ?? currentTenantId.value);

const searchQuery = ref("");
const searchInput = ref<HTMLInputElement | null>(null);

onMounted(() => searchInput.value?.focus());

// Normalize for search: strip diacritics + lowercase, so "café" matches "Cafe".
const normalize = (text: string): string =>
  text
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();

const WORD_SEPARATORS = /[\s\-_./,&]+/;

// Score how well a tenant name matches the search tokens.
// Returns 0 when any token is missing, otherwise a positive rank: higher is better.
//   100 — name starts with the full query (e.g. "acm" → "Acme Corp")
//    50 — a word inside the name starts with the full query (e.g. "corp" → "Acme Corporation")
//    10 — every token appears somewhere in the name (substring fallback)
const scoreMatch = (name: string, tokens: string[], fullQuery: string): number => {
  const normalizedName = normalize(name);

  // Every token must appear somewhere in the name, otherwise this tenant is filtered out.
  if (!tokens.every((token) => normalizedName.includes(token))) return 0;

  if (normalizedName.startsWith(fullQuery)) return 100;
  if (normalizedName.split(WORD_SEPARATORS).some((word) => word.startsWith(fullQuery))) return 50;
  return 10;
};

const filteredTenants = computed(() => {
  const rawQuery = searchQuery.value.trim();
  if (!rawQuery) return tenants.value;

  const fullQuery = normalize(rawQuery);
  // Split on whitespace so "acme corp" requires both "acme" and "corp" to match (AND).
  const tokens = fullQuery.split(/\s+/).filter(Boolean);

  return tenants.value
    .map((tenant) => ({ tenant, score: scoreMatch(tenant.name, tokens, fullQuery) }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .map(({ tenant }) => tenant);
});

const switchingToTenantId = ref<string | null>(null);
const isSwitching = computed(() => switchingToTenantId.value !== null);

const waitForFronteggTenant = (targetTenantId: string, timeoutMs = 6000): Promise<boolean> =>
  new Promise((resolve) => {
    const start = Date.now();
    const tick = () => {
      if (authStore.user?.tenantId === targetTenantId) return resolve(true);
      if (Date.now() - start >= timeoutMs) return resolve(false);
      setTimeout(tick, 50);
    };
    tick();
  });

// The new tenant's Hasura JWT is minted asynchronously after Frontegg confirms the switch, so
// "Frontegg reports the new tenant" does not mean Apollo carries the new identity yet. Waiting
// only on the timeout (mint failed or produced the identical token) degrades to today's behavior;
// the store's terminate-on-token watcher then self-heals once the token eventually lands.
const waitForNewHasuraToken = (previousToken: string | undefined, timeoutMs = 6000): Promise<boolean> =>
  new Promise((resolve) => {
    const start = Date.now();
    const tick = () => {
      const token = hasuraStore.hasuraToken;
      if (token && token !== previousToken) return resolve(true);
      if (Date.now() - start >= timeoutMs) return resolve(false);
      setTimeout(tick, 50);
    };
    tick();
  });

const switchTenant = async (tenantId: string) => {
  if (!tenantId || tenantId === currentTenantId.value || isSwitching.value) return;

  switchingToTenantId.value = tenantId;
  isSwitchingTenant.value = true;

  // Optimistically reflect the new org name in the UI immediately. We already
  // know which tenant the user picked, so the TopNav can update before Frontegg
  // finishes the switch. If the switch fails later, App.vue's auth watch will
  // reconcile back to the real current tenant.
  const nextTenant = orgStore.getTenants()?.find((tenant) => tenant.tenantId === tenantId);
  if (nextTenant) orgStore.setCurrentTenant(nextTenant);

  // Close the dropdown immediately so the user sees the BaseView overlay,
  // not a stuck dropdown with its own spinner.
  emit("close");

  try {
    // Navigate to "/" first so any project-scoped components unmount before
    // Frontegg's auth state changes underneath them. Their reactive queries
    // would otherwise re-run mid-switch and crash on stale tenant context.
    if (router.currentRoute.value.path !== "/") {
      await router.push({ path: "/" });
    }

    const previousHasuraToken = hasuraStore.hasuraToken;

    mapAuthActions("switchTenant")({ tenantId });

    // Wait until Frontegg actually reports the new tenant in user state
    await waitForFronteggTenant(tenantId);

    // Clear per-project material filters cached in localStorage
    Object.keys(localStorage)
      .filter((key) => key.includes("-material-filters-"))
      .forEach((key) => localStorage.removeItem(key));

    // resetStore() refetches every active query immediately. Doing that before the new tenant's
    // Hasura JWT has replaced the old one in Apollo's defaultContext would repopulate the freshly
    // cleared cache with the PREVIOUS org's rows (RTLCA-5588). The nextTick lets hasura.store's
    // watcher write the settled token into defaultContext before the refetches build requests.
    await waitForNewHasuraToken(previousHasuraToken);
    await nextTick();

    // Drop GraphQL caches so subsequent queries fetch under the new tenant. In-flight queries are
    // cancelled with Apollo's store-reset error, which reaches their own callers, not this catch.
    try {
      await gaiupApolloClient.resetStore();
    } catch {
      // a refetch failing here is reported by the query's own consumer
    }
  } finally {
    switchingToTenantId.value = null;
    // Keep the suppression window open one more tick in case Frontegg fires
    // a trailing transient error right after we settle.
    setTimeout(() => {
      isSwitchingTenant.value = false;
    }, 300);
  }
};
</script>

<template>
  <div
    class="rounded-8 z-dropdown bg-slate-0 absolute top-full left-0 mt-2 flex w-72 flex-col overflow-hidden ring-1 ring-slate-200"
    role="dialog"
    aria-label="Switch organization">
    <div class="gap-px-8 flex items-center border-b border-slate-200 py-0.5">
      <input
        ref="searchInput"
        v-model="searchQuery"
        type="text"
        placeholder="Find organizations..."
        aria-label="Find organizations"
        class="t-small placeholder:text-type-muted/70 flex-1 bg-transparent px-4 py-2 outline-none" />
    </div>

    <div
      class="max-h-72 overflow-y-auto p-1"
      @mouseleave="hoveredTenantId = null">
      <button
        v-for="tenant in filteredTenants"
        :key="tenant.tenantId"
        type="button"
        :aria-current="tenant.tenantId === currentTenantId ? 'true' : undefined"
        class="focus-ring interactive-active gap-px-8 text-type-default duration-fast ease-standard flex w-full items-center rounded-md px-3 py-2 text-left transition-colors"
        :class="tenant.tenantId === activeTenantId ? 'bg-app-blue-200/20' : ''"
        @mouseenter="hoveredTenantId = tenant.tenantId"
        @click="switchTenant(tenant.tenantId)">
        <span
          class="t-small flex-1 truncate text-inherit"
          :class="tenant.tenantId === currentTenantId ? 't-small-bold' : ''">
          {{ tenant.name }}
        </span>
        <Check
          v-if="tenant.tenantId === currentTenantId"
          :stroke-width="1.5"
          class="icon-sm text-app-blue-600 shrink-0"
          aria-hidden="true" />
      </button>

      <p
        v-if="filteredTenants.length === 0"
        class="t-small text-type-muted px-3 py-4 text-center">
        No organizations found
      </p>
    </div>
  </div>
</template>
